import { Check, ChevronsUpDown, Plus, X } from 'lucide-react';
import { KeyboardEvent, useCallback, useMemo, useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface MultiSelectAutocompleteOption {
  value: string;
  label: string;
  description?: string;
}

interface MultiSelectAutocompleteProps {
  /** Currently selected values (the actual stored representation). */
  value: string[];
  /** Called whenever the selection changes. */
  onChange: (next: string[]) => void;
  /** The catalog of known options. Optional - when empty, free-text only. */
  options?: MultiSelectAutocompleteOption[];
  /** Placeholder shown in the inline typeahead input. */
  placeholder?: string;
  /** Empty state inside the dropdown. */
  emptyMessage?: string;
  /** If true, only catalog values can be picked (no free-text). */
  strict?: boolean;
  /** ARIA disabled state. */
  disabled?: boolean;
  /** Container className. */
  className?: string;
}

export function MultiSelectAutocomplete({
  value,
  onChange,
  options = [],
  placeholder = 'Type to search or add…',
  emptyMessage = 'No matches.',
  strict = false,
  disabled = false,
  className,
}: MultiSelectAutocompleteProps) {
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [typeaheadOpen, setTypeaheadOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Quick lookup: option-value -> label (for chip display).
  const labelByValue = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const hasCatalog = options.length > 0;

  const trimmedSearch = search.trim();
  const exactMatch = useMemo(
    () =>
      options.find(
        (o) =>
          o.value.toLowerCase() === trimmedSearch.toLowerCase() ||
          o.label.toLowerCase() === trimmedSearch.toLowerCase(),
      ),
    [options, trimmedSearch],
  );

  const filteredOptions = useMemo(() => {
    if (!trimmedSearch) return options.filter((o) => !selectedSet.has(o.value));
    const q = trimmedSearch.toLowerCase();
    return options.filter(
      (o) =>
        !selectedSet.has(o.value) &&
        (o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q)),
    );
  }, [options, trimmedSearch, selectedSet]);

  const addValue = useCallback(
    (raw: string) => {
      const v = raw.trim();
      if (!v) return;
      // Resolve via catalog first - if the user typed a known label, store the value.
      const opt = options.find(
        (o) =>
          o.value.toLowerCase() === v.toLowerCase() ||
          o.label.toLowerCase() === v.toLowerCase(),
      );
      const resolved = opt ? opt.value : v;
      if (selectedSet.has(resolved)) return;
      if (strict && !opt) return;
      onChange([...value, resolved]);
      setSearch('');
    },
    [options, selectedSet, strict, onChange, value],
  );

  const removeValue = useCallback(
    (target: string) => {
      onChange(value.filter((v) => v !== target));
    },
    [onChange, value],
  );

  const togglePicker = useCallback(
    (target: string) => {
      if (selectedSet.has(target)) {
        removeValue(target);
      } else {
        onChange([...value, target]);
      }
    },
    [selectedSet, removeValue, onChange, value],
  );

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Prefer the exact catalog match if there is one; otherwise add free-text.
      if (exactMatch) {
        addValue(exactMatch.value);
      } else if (!strict) {
        addValue(trimmedSearch);
      }
    } else if (e.key === 'Backspace' && !search && value.length > 0) {
      // Convenience: backspace on empty input removes the last chip.
      removeValue(value[value.length - 1]);
    } else if (e.key === ',' || e.key === 'Tab') {
      // Comma + Tab as separators (paste-friendly when bulk-adding).
      if (trimmedSearch) {
        e.preventDefault();
        if (exactMatch) addValue(exactMatch.value);
        else if (!strict) addValue(trimmedSearch);
      }
    }
  };

  const showAddRow =
    hasCatalog &&
    !!trimmedSearch &&
    !exactMatch &&
    !strict &&
    !selectedSet.has(trimmedSearch);

  return (
    <div className={cn('w-full', className)}>
      {/* Pills + inline input + chevron toggle */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1.5 text-sm shadow-xs transition-[color,box-shadow]',
          'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
          disabled && 'pointer-events-none opacity-60',
        )}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((v) => (
          <Badge
            key={v}
            variant='secondary'
            className='gap-1 pr-1 font-mono text-[11px]'
            title={v}
          >
            <span className='max-w-[180px] truncate'>
              {labelByValue.get(v) ?? v}
            </span>
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                removeValue(v);
              }}
              className='ml-0.5 rounded hover:bg-background/80 transition-colors'
              aria-label={`Remove ${v}`}
              tabIndex={-1}
            >
              <X className='size-3' />
            </button>
          </Badge>
        ))}

        <div className='relative flex-1 min-w-[140px]'>
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setTypeaheadOpen(true);
            }}
            onFocus={() => setTypeaheadOpen(true)}
            onBlur={() => {
              // Defer so a click on the dropdown row still fires.
              setTimeout(() => setTypeaheadOpen(false), 120);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={value.length === 0 ? placeholder : ''}
            disabled={disabled}
            className='w-full bg-transparent outline-none placeholder:text-muted-foreground text-xs font-mono'
          />

          {/* Typeahead suggestions */}
          {typeaheadOpen && (filteredOptions.length > 0 || showAddRow) && (
            <div className='absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md'>
              {filteredOptions.slice(0, 8).map((o) => (
                <button
                  type='button'
                  key={o.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addValue(o.value);
                  }}
                  className='flex w-full items-start gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground text-left'
                >
                  <Plus className='size-3.5 mt-0.5 text-muted-foreground shrink-0' />
                  <span className='flex-1 min-w-0'>
                    <span className='block truncate'>{o.label}</span>
                    {o.description && (
                      <span className='block truncate text-xs text-muted-foreground'>
                        {o.description}
                      </span>
                    )}
                  </span>
                </button>
              ))}
              {showAddRow && (
                <button
                  type='button'
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addValue(trimmedSearch);
                  }}
                  className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground border-t border-border/50 mt-1'
                >
                  <Plus className='size-3.5 text-muted-foreground' />
                  <span>
                    Add custom{' '}
                    <span className='font-mono'>"{trimmedSearch}"</span>
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* Chevron - opens full multi-select picker */}
        {hasCatalog && (
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='size-6 shrink-0 text-muted-foreground hover:text-foreground'
                onClick={(e) => e.stopPropagation()}
                disabled={disabled}
                aria-label='Open multi-select picker'
                type='button'
              >
                <ChevronsUpDown className='size-4' />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className='p-0'
              align='end'
              style={{ width: 'min(320px, calc(100vw - 2rem))' }}
            >
              <Command>
                <CommandInput placeholder='Search…' />
                <CommandList>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>
                  <CommandGroup>
                    {options.map((o) => {
                      const isSelected = selectedSet.has(o.value);
                      return (
                        <CommandItem
                          key={o.value}
                          value={`${o.label} ${o.value}`}
                          onSelect={() => togglePicker(o.value)}
                        >
                          <Check
                            className={cn(
                              'mr-2 size-4',
                              isSelected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <div className='flex-1 min-w-0'>
                            <div className='truncate'>{o.label}</div>
                            {o.description && (
                              <div className='truncate text-xs text-muted-foreground'>
                                {o.description}
                              </div>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
